import React, { useCallback, useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import { useSetRecoilState } from 'recoil';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import Alert from 'up-ops-web-shared-components/lib/components/alert';
import RadioButton from 'up-ops-web-shared-components/lib/components/radio/radio-button';
import { smallText } from 'up-ops-web-shared-components/lib/styles/typography';

import FormFileUpload from 'components/FormFileUpload';
import FormFormattedInput from 'components/FormFormattedInput';
import FormHeader from 'components/FormHeader';
import FormTextInput from 'components/FormTextInput';
import { FormRow } from 'styles/CommonStyles';
import { TwoAnswerRadioGroup } from 'components/FormRadioGroup';
import FormSelectInput from 'components/FormSelectInput';
import FormDatePicker from 'components/FormDatePicker';
import FormSubmit from 'components/FormSubmit';
import FormTracker from 'components/FormTracker';
import { acceptedFiles, FormIdentifier } from 'lib/constants';
import { normalizePhoneNumber } from 'lib/utils';
import { patterns } from 'lib/validation';
import useUploadActions from 'hooks/upload';
import useSubmitActions from 'hooks/submit';
import { showSubmitSuccessState } from 'state/atoms/submit';

export const MAILING_ADDRESS =
  'Loan Holder, c/o Flex Pay, 2 N Central Ave, 10th Floor, Phoenix, AZ 85004';

const StyledAlert = styled(Alert)`
  margin: 1rem 0 1.5rem;
  padding: 12px 14px;
  width: 100%;
  ${smallText}
`;

const StyledMailingInstructions = styled.div`
  border-top: 1px solid ${(props) => props.theme.colors.borderDefault};
  font-size: ${(props) => props.theme.font.size.s.fontSize};
  margin-top: 55px;
  padding-top: 1rem;

  b {
    color: ${(props) => props.theme.colors.textDark};
    display: block;
    font-weight: ${(props) => props.theme.font.weight.normal};
  }
`;

const BankruptcyForm = () => {
  const { submitForm } = useSubmitActions();
  const { uploadFiles } = useUploadActions();
  const { t, i18n } = useTranslation();
  const formMethods = useForm();
  const formSessionUuid = useRef<string>('');
  const setShowSuccess = useSetRecoilState(showSubmitSuccessState);

  useEffect(() => {
    formSessionUuid.current = uuidv4();
  }, []);

  const onSubmit = useCallback(
    (data: any) => {
      const {
        bankruptcyChapter,
        courtsFiling,
        documents,
        phoneNumber,
        ...otherData
      } = data;
      const formData = {
        formType: FormIdentifier.BANKRUPTCY,
        bankruptcyChapter: bankruptcyChapter?.value,
        phoneNumber: normalizePhoneNumber(phoneNumber),
        ...otherData,
      };

      if (courtsFiling === 'yes') {
        formData.filingDetails = {
          caseNumber: otherData.caseNumber,
          districtCourt: otherData.districtCourt,
          filingDate: otherData.filingDate,
        };
        delete formData.caseNumber;
        delete formData.districtCourt;
        delete formData.filingDate;
      }

      if (documents && documents.length > 0) {
        return uploadFiles(formSessionUuid.current, 'servicing', documents)
          .then((uploadedDocs) =>
            submitForm(
              formSessionUuid.current,
              { ...formData, documents: uploadedDocs },
              i18n.language
            )
          )
          .then(() => setShowSuccess(true));
      } else {
        return submitForm(
          formSessionUuid.current,
          formData,
          i18n.language
        ).then(() => setShowSuccess(true));
      }
    },
    [formSessionUuid, i18n.language, setShowSuccess, submitForm, uploadFiles]
  );

  const [watchedCourtsFiling] = formMethods.watch(['courtsFiling']);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <FormHeader
          description={t('forms.bankruptcy.description')}
          title={t('forms.bankruptcy.name')}
        />
        <FormRow>
          <FormTextInput
            label={t<string>('labels.name')}
            name="name"
            rules={{
              pattern: {
                value: patterns.noSpecialCharacters,
                message: t<string>('validation.invalid'),
              },
              required: t<string>('validation.required'),
            }}
          />
          <FormTextInput
            label={t<string>('labels.emailAddress')}
            name="email"
            rules={{
              pattern: {
                message: t<string>('validation.emailFormat'),
                value: patterns.emailAddress,
              },
              required: t<string>('validation.required'),
            }}
          />
        </FormRow>
        <FormRow>
          <FormTextInput
            label={t<string>('labels.phoneNumber')}
            name="phoneNumber"
            rules={{
              pattern: {
                message: t<string>('validation.phoneNumberFormat'),
                value: patterns.phoneNumber,
              },
              required: t<string>('validation.required'),
            }}
          />
          <FormFormattedInput
            label={t<string>('labels.ssnDigits')}
            maxLength={4}
            name="stableId"
            obscurable
            placeholder="&bull;&bull;&bull;&bull;"
            rules={{
              pattern: {
                message: t<string>('validation.ssnDigitsFormat'),
                value: patterns.lastFourDigits,
              },
              required: t<string>('validation.required'),
            }}
          />
        </FormRow>
        <FormRow>
          <TwoAnswerRadioGroup
            name="courtsFiling"
            title={t<string>('labels.courtsFiling')}
            rules={{ required: t<string>('validation.courtsFilingRequired') }}
          >
            <RadioButton label={t('common.yes')} value="yes" />
            <RadioButton label={t('common.no')} value="no" />
          </TwoAnswerRadioGroup>
        </FormRow>
        {watchedCourtsFiling === 'no' && (
          <StyledAlert borderless success>
            {t('forms.bankruptcy.noCourtFilingsAlert')}
          </StyledAlert>
        )}
        {watchedCourtsFiling === 'yes' && (
          <>
            <FormRow>
              <FormTextInput
                name="districtCourt"
                label={t<string>('labels.districtCourt')}
                rules={{
                  pattern: {
                    value: patterns.noSpecialCharacters,
                    message: t<string>('validation.invalid'),
                  },
                  required: t<string>('validation.required'),
                }}
              />
              <FormTextInput
                name="caseNumber"
                label={t<string>('labels.caseNumber')}
                rules={{ required: t<string>('validation.required') }}
              />
            </FormRow>
            <FormRow>
              <FormDatePicker
                name="filingDate"
                label={t<string>('labels.caseFiledDate')}
                rules={{ required: t<string>('validation.required') }}
              />
            </FormRow>
          </>
        )}
        <FormRow>
          <FormSelectInput
            name="bankruptcyChapter"
            label={t<string>('labels.bankruptcyChapter')}
            rules={{ required: t<string>('validation.required') }}
          >
            <option disabled value="">
              &nbsp;
            </option>
            <option value="Chapter7">7</option>
            <option value="Chapter11">11</option>
            <option value="Chapter13">13</option>
          </FormSelectInput>
          <FormTextInput
            label={t<string>('labels.attorneyName')}
            name="attorneyName"
            rules={{
              pattern: {
                value: patterns.noSpecialCharacters,
                message: t<string>('validation.invalid'),
              },
              required: t<string>('validation.required'),
            }}
          />
        </FormRow>
        <FormRow>
          <div>
            <FormFileUpload
              acceptTypes={acceptedFiles}
              ariaLabel={t('labels.uploadDocumentsOptional')}
              label={t('labels.uploadDocumentsOptional')}
              name="documents"
            />
          </div>
        </FormRow>
        <FormSubmit formType={FormIdentifier.BANKRUPTCY} />
        <FormTracker formType={FormIdentifier.BANKRUPTCY} />
      </form>
      <StyledMailingInstructions>
        <Trans
          i18nKey="prompts.mailAttorneyInstructions"
          values={{ mailingAddress: MAILING_ADDRESS }}
        >
          In case the Attorney/Borrower wants to mail a copy of the case to
          Uplift, please use the following address
          <b>{MAILING_ADDRESS}</b>
        </Trans>
      </StyledMailingInstructions>
    </FormProvider>
  );
};

export default BankruptcyForm;
